// ─── Identity re-key: carry my own state across a changed user id (#2301) ────
//
// The relay is about to start minting the user id into the login token
// (`CYBORG7_MINT_USER_ID_IN_TOKEN`, default OFF). The moment it does, a server
// that used to answer `cyborg:auth` with `canonicalUserId(email)` re-keys the
// same human onto their cloud id — so `authResp.user.id` CHANGES between one
// login and the next, for the same person, on the same server.
//
// Everything per-user on this client is keyed by that id: eight
// `<base>_<userId>` localStorage namespaces (favorites, opened DMs, DM activity,
// unread flags, notification counts, user status, activity-read markers, client
// notify prefs) plus the persistent send outbox in IndexedDB. After a re-key
// they all orphan — the stars are "gone", and a queued unsent message is never
// replayed, never shown as failed and never deleted. It silently vanishes.
//
// TWO conditions must hold before any of it is carried across, and both are
// load-bearing:
//
//   • the OLD id is one the SERVER itself says is this human — `canonicalIds` is
//     the relay's own `sameHumanIds(user.id, user.email)`, derived from their
//     VERIFIED address (see session-scope.ts). A different human's id can never
//     appear in it, so a shared-device user switch can never inherit the
//     previous account's stars or, worse, replay their queued sends.
//
//   • it is the id THIS SERVER issued last time. One human legitimately holds a
//     DIFFERENT id per server today — cloud vs self-hosted, which is exactly why
//     `cacheInit` is keyed on the email — and that is NOT a re-key: those queued
//     sends belong to the other endpoint and must not follow you there. The
//     server-url anchor is also what keeps this a strict no-op until the mint
//     flag is actually turned on: with the flag off, no server ever changes the
//     id it hands back, so `prev === viewer.id` on every login forever.
//
// No UI, no versioned-storage subsystem: a re-key is a rename, so this renames.

/** Per-server memory of the id it last issued — a CHANGE of it is the signal. */
const PREV_USER_ID_BASE = "cyborg7_prev_user_id";

/** The `authResp.user` fields this needs. Structural so tests need no fixture. */
export interface RekeyViewer {
  id: string;
  canonicalIds?: readonly string[] | null;
}

/**
 * Record the id `serverUrl` just issued, and — when it REPLACED a different id
 * that provably names the same human — copy every `<base>_<oldId>` localStorage
 * namespace onto the new id.
 *
 * Returns the previous id when that happened, so the caller can adopt the
 * IndexedDB send outbox with it too; null otherwise (the overwhelmingly common
 * case, including every login while the mint flag is off).
 *
 * COPIES rather than moves: the mint is behind a kill switch that can be turned
 * back OFF, which reverts the id. Leaving the old namespaces in place makes that
 * rollback lossless too, and they are keys this same account already owned.
 */
export function migrateRekeyedIdentity(serverUrl: string, viewer: RekeyViewer): string | null {
  if (typeof localStorage === "undefined" || !viewer.id) return null;
  try {
    const seenKey = `${PREV_USER_ID_BASE}_${serverUrl}`;
    const prev = localStorage.getItem(seenKey);
    localStorage.setItem(seenKey, viewer.id);
    if (!prev || prev === viewer.id) return null;
    // Same server, different id — but only the server's own same-human set makes
    // it MINE. An older relay omits `canonicalIds` entirely and we do nothing.
    if (!(viewer.canonicalIds ?? []).includes(prev)) return null;
    const suffix = `_${prev}`;
    // Snapshot the key list first: we write into the same storage while looping,
    // and Storage indices shift on insert.
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
    for (const key of keys) {
      if (!key?.endsWith(suffix)) continue;
      const value = localStorage.getItem(key);
      if (value !== null) {
        localStorage.setItem(`${key.slice(0, -suffix.length)}_${viewer.id}`, value);
      }
    }
    return prev;
  } catch {
    // intentional: best-effort carry-over (private mode / quota). Returning null
    // leaves the client on the status quo — orphaned, never corrupted.
    return null;
  }
}
